import {
  CellContext,
  ColumnDef,
  createColumnHelper,
} from '@tanstack/react-table';
import { RequiredText, TextEllipsis } from '../../common/components';
import { sortStringLower } from '../../common/utils';
import type { DataObjectValidFormDataType } from '../schema';
import {
  DataObjectDimensionType,
  DataObjectOutputType,
  DataObjectType,
  DataObjectView,
  DimensionValidFormDataType,
} from '../types';
import {
  CloneFormTableRowButton,
  DeleteFormTableRowButton,
  FormCheckbox,
  FormField,
  FormInput,
  FormInputNumber,
  FormTableSequenceNumberField,
  getFormTableFieldName,
  TanstackFormTable,
} from '../../common/form';
import { useFormContext, useWatch } from 'react-hook-form';
import React, { FC, memo, ReactNode, useEffect, useMemo } from 'react';
import translations from '../translations/DataObjectDimensionTable.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { TFunction } from 'i18next';
import { useSystemClassifierSelectOptions } from '../../configuration';
import { CreateDimensionRowButton } from './CreateDimensionRowButton';
import { DimensionClassifierValue } from './DimensionClassifierValue';
import { ClassifierGroupSelect } from './ClassifierGroupSelect';
import { Flex } from 'antd';
import { classifierFilterFn, classifierSortingFn } from './utils';
import { useSystemClassifiers } from '../../configuration/useSystemClassifiers';
import { AggregateHierarchyCheckbox } from './AggregateHierarchyCheckbox';

const { addTranslations, t, useTranslation } = i18nNsFactory(
  'data-object-dimension-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<DimensionValidFormDataType>();

const renumberDimensions = (dimensions: DimensionValidFormDataType[]) =>
  dimensions.map((dimension, index) => ({
    ...dimension,
    sequenceNumber: index + 1,
  }));

function createColumn(
  column: 'code' | 'name' | 'description' | 'width',
  isEditable: (row: DimensionValidFormDataType) => boolean,
  required = false,
  width?: number | string,
  minWidth?: number | string,
  placeholder?: number | string,
) {
  return columnHelper.accessor(column, {
    header: required
      ? () => <RequiredText>{t(`field.${column}.label`)}</RequiredText>
      : t(`field.${column}.label`),
    cell: (props) => (
      <DefaultCell
        {...props}
        isEditable={isEditable}
        placeholder={placeholder}
      />
    ),
    filterFn: 'includesString',
    meta: {
      width,
      minWidth,
    },
  });
}

function getColumns(
  t: TFunction,
  dataObjectView: DataObjectView,
  isDraft: boolean,
  outputTypeCode?: DataObjectOutputType,
  subjects?: string[],
  validPeriod?: [string | null, string | null],
  addRowButton?: ReactNode | undefined,
): ColumnDef<DimensionValidFormDataType, any>[] {
  const isEditable = (dimension: DimensionValidFormDataType) =>
    !!subjects?.every((subject) => dimension.code !== subject);
  const isEditableDraft = (dimension: DimensionValidFormDataType) =>
    isDraft && isEditable(dimension);
  const isDisplayedRequired = (dimension: DimensionValidFormDataType) =>
    dimension.dimensionType?.code === DataObjectDimensionType.SUBJECT ||
    (dataObjectView.dataObjectType === DataObjectType.OUT &&
      dimension.dimensionType?.code !==
        DataObjectDimensionType.SUBJECT_ATTRIBUTE) ||
    (dataObjectView.dataObjectType === DataObjectType.IN &&
      dimension.dimensionType?.code === DataObjectDimensionType.CLASSIFIER);
  const isEditableRequired = (dimension: DimensionValidFormDataType) =>
    isDraft &&
    dimension.dimensionType?.code === DataObjectDimensionType.CLASSIFIER;

  const columns: ColumnDef<DimensionValidFormDataType, any>[] = [
    columnHelper.accessor('sequenceNumber', {
      header: t('field.sequenceNumber.label'),
      cell: FormTableSequenceNumberField,
      meta: {
        width: 110,
        className: 'sticky-left',
        textAlign: 'right',
      },
      filterFn: 'includesString',
      sortingFn: 'basic',
    }),
    columnHelper.accessor((rowData) => rowData.dimensionType?.name, {
      id: 'dimensionType',
      header: t('field.dimensionType.label'),
      cell: (props) => props.renderValue(),
      filterFn: 'includesOption',
      meta: {
        className: 'sticky-left',
        width: '20ch',
      },
      sortingFn: (a, b) =>
        sortStringLower(
          a.original.dimensionType?.name ?? null,
          b.original.dimensionType?.name ?? null,
        ),
    }),
    columnHelper.accessor('classifier', {
      header: t('field.classifier.label'),
      cell: (props) => (
        <ClassifierCell
          {...props}
          validPeriod={validPeriod}
          isEditableDraft={isEditableDraft}
        />
      ),
      filterFn: classifierFilterFn,
      meta: {
        width: '30%',
        minWidth: 200,
      },
      sortingFn: classifierSortingFn,
    }),
    createColumn('code', isEditableDraft, true, '30ch'),
    createColumn('name', isEditable, true, '40%', 150),
    createColumn('description', isEditable, false, '30%', 150),
  ];
  columns.push(
    columnHelper.accessor('width', {
      header: () => <RequiredText>{t(`field.width.label`)}</RequiredText>,
      cell: WidthCell,
      meta: {
        width: 130,
        textAlign: 'right',
      },
    }),
  );
  if (dataObjectView === DataObjectView.OUT) {
    columns.push(
      columnHelper.accessor('constantValue', {
        header: t('field.constant.label'),
        cell: (props) => (
          <ConstantValueCell {...props} isEditableDraft={isEditableDraft} />
        ),
        filterFn: 'includesString',
        meta: {
          width: 150,
        },
        sortingFn: (a, b) => {
          return sortStringLower(
            a.original.constantValue,
            b.original.constantValue,
          );
        },
      }),
    );
  }
  columns.push(
    columnHelper.accessor('required', {
      header: t('field.required.label'),
      cell: (props) => (
        <RequiredCell
          {...props}
          isDisplayedRequired={isDisplayedRequired}
          isEditableRequired={isEditableRequired}
        />
      ),
      meta: { width: 90 },
      enableColumnFilter: false,
      enableSorting: false,
    }),
  );
  if (
    dataObjectView === DataObjectView.OUT &&
    outputTypeCode !== DataObjectOutputType.REPORT
  ) {
    columns.push(
      columnHelper.accessor('aggregateHierarchy', {
        header: t('field.aggregateHierarchy.label'),
        cell: (props) => (
          <AggregateHierarchyCell {...props} isDraft={isDraft} />
        ),
        meta: { width: '17ch' },
        enableColumnFilter: false,
        enableSorting: false,
      }),
    );
  }
  columns.push(
    columnHelper.display({
      id: 'operations',
      header: () => <Flex justify="center">{addRowButton}</Flex>,
      enableSorting: false,
      enableColumnFilter: false,
      cell: (props) => <OperationsCell {...props} isDraft={isDraft} />,
      meta: {
        width: 88,
        className: 'sticky-right',
      },
    }),
  );

  return columns;
}

const DefaultCell: FC<
  CellContext<DimensionValidFormDataType, any> & {
    isEditable: (row: DimensionValidFormDataType) => boolean;
    placeholder?: number | string;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  const disabled = !props.isEditable(props.row.original);
  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (val) => {
          return <TextEllipsis>{val}</TextEllipsis>;
        },
      }}
      disabled={disabled}
    >
      <FormInput
        size="small"
        name={name}
        disabled={disabled}
        placeholder={
          typeof props.placeholder == 'undefined'
            ? ''
            : String(props.placeholder)
        }
        autoFocus
      />
    </FormField>
  );
});

const ClassifierCell: FC<
  CellContext<DimensionValidFormDataType, any> & {
    validPeriod?: [string | null, string | null];
    isEditableDraft: (row: DimensionValidFormDataType) => boolean;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  if (
    props.isEditableDraft(props.row.original) &&
    props.row.original.dimensionType?.code ===
      DataObjectDimensionType.CLASSIFIER
  ) {
    return (
      <FormField
        name={name}
        size="small"
        preview={{
          render: (value) => <DimensionClassifierValue {...value} />,
          ellipsis: false,
        }}
      >
        <ClassifierGroupSelect
          size="small"
          autoFocus
          defaultOpen
          name={name}
          validPeriod={props.validPeriod}
        />
      </FormField>
    );
  }

  return (
    <FormField size="small" name={name}>
      <DimensionClassifierValue {...props.row.original.classifier} />
    </FormField>
  );
});

const WidthCell: FC<CellContext<DimensionValidFormDataType, any>> = memo(
  (props) => {
    const name = getFormTableFieldName(props);
    return (
      <FormField size="small" name={name} preview>
        <FormInputNumber
          size="small"
          name={name}
          min="1"
          max="999"
          decimalSeparator=","
          autoFocus
        />
      </FormField>
    );
  },
);

const ConstantValueCell: FC<
  CellContext<DimensionValidFormDataType, any> & {
    isEditableDraft: (row: DimensionValidFormDataType) => boolean;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  const disabled = !(
    props.isEditableDraft(props.row.original) &&
    props.row.original.dimensionType?.code === DataObjectDimensionType.CONSTANT
  );
  return (
    <FormField
      name={name}
      size="small"
      preview={{
        render: (val) => {
          return <TextEllipsis>{val}</TextEllipsis>;
        },
      }}
      disabled={disabled}
    >
      <FormInput size="small" name={name} disabled={disabled} autoFocus />
    </FormField>
  );
});

const RequiredCell: FC<
  CellContext<DimensionValidFormDataType, any> & {
    isDisplayedRequired: (row: DimensionValidFormDataType) => boolean;
    isEditableRequired: (row: DimensionValidFormDataType) => boolean;
  }
> = memo((props) => {
  if (!props.isDisplayedRequired(props.row.original)) {
    return null;
  }

  const name = getFormTableFieldName(props);
  const disabled = !props.isEditableRequired(props.row.original);

  return (
    <Flex justify="center" align="center">
      <FormField size="small" name={name} preview={false} disabled={disabled}>
        <FormCheckbox name={name} disabled={disabled} />
      </FormField>
    </Flex>
  );
});

const AggregateHierarchyCell: FC<
  CellContext<DimensionValidFormDataType, any> & {
    isDraft: boolean;
  }
> = memo((props) => {
  const name = getFormTableFieldName(props);
  return (
    <Flex justify="center" align="center">
      <FormField size="small" name={name} preview={false}>
        <AggregateHierarchyCheckbox name={name} isDraft={props.isDraft} />
      </FormField>
    </Flex>
  );
});

const OperationsCell: FC<
  CellContext<DimensionValidFormDataType, any> & {
    isDraft: boolean;
  }
> = memo((props) => {
  const isSubject = props.row.original.code === 'SUBJEKT';
  const isDeletable = props.isDraft && !isSubject;
  return (
    <Flex justify="center" gap="small">
      {isDeletable && (
        <>
          <CloneFormTableRowButton
            context={props}
            resetValues={{ aggregateHierarchy: false }}
            resetId
            autoFocusField="classifier"
          />
          <DeleteFormTableRowButton context={props} />
        </>
      )}
    </Flex>
  );
});

export function DataObjectDimensionTable({
  dataObjectView,
  outputTypeCode,
  defaultStatusCode,
}: {
  dataObjectView: DataObjectView;
  outputTypeCode?: DataObjectOutputType;
  defaultStatusCode: string;
}) {
  const { DATA_SUBJECT_ATTRIBUTE } = useSystemClassifierSelectOptions(
    ['DATA_SUBJECT_ATTRIBUTE'],
    ({ code, name }) => ({ code, name }),
  );

  const {
    DIMENSION_TYPE: { systemClassifierItems: dimensionTypes },
    DEFAULT_INDICATOR_DIMENSION_WIDTH: {
      systemClassifierItems: [defaultWidth],
    },
  } = useSystemClassifiers(
    'DIMENSION_TYPE',
    'DEFAULT_INDICATOR_DIMENSION_WIDTH',
  );

  const subjects = useMemo(
    () => ['SUBJEKT', ...DATA_SUBJECT_ATTRIBUTE.map(({ code }) => code)],
    [DATA_SUBJECT_ATTRIBUTE],
  );

  const subjectDimension = useWatch({ name: 'subjectDimension' });

  const { cols, filters, isDraft } = useDimensionTable(
    dataObjectView,
    defaultStatusCode,
    subjects,
    outputTypeCode,
    subjectDimension ? DATA_SUBJECT_ATTRIBUTE : [],
  );
  const form = useFormContext<DataObjectValidFormDataType>();

  useEffect(() => {
    if (dataObjectView === DataObjectView.OUT) {
      const dimensions = form.getValues('dimensions') || [];
      if (!subjectDimension) {
        const noSubjectDimensions = dimensions.filter(
          (dimension) => !subjects.includes(dimension.code),
        );
        if (noSubjectDimensions.length !== dimensions.length) {
          form.setValue('dimensions', renumberDimensions(noSubjectDimensions));
        }
      } else if (
        !dimensions.some((dimension) => dimension.code === 'SUBJEKT') &&
        isDraft
      ) {
        const subjectDimension = {
          code: 'SUBJEKT',
          name: 'Andmesubjekt',
          description: null,
          sequenceNumber: 1,
          classifier: null,
          constantValue: null,
          aggregateHierarchy: null,
          width: Number(defaultWidth.code),
          required: true,
          dimensionType:
            dimensionTypes.find((item) => item.code === 'SUBJECT') ?? null,
        } as DimensionValidFormDataType;
        form.setValue(
          'dimensions',
          renumberDimensions([subjectDimension, ...dimensions]),
        );
      }
    }
  }, [
    dataObjectView,
    form,
    subjectDimension,
    isDraft,
    subjects,
    defaultWidth.code,
    dimensionTypes,
  ]);

  useEffect(() => {
    if (
      dataObjectView === DataObjectView.OUT &&
      outputTypeCode !== DataObjectOutputType.REPORT
    ) {
      const dimensions = form.getValues('dimensions') || [];
      const noKeyDimensions = dimensions.filter(
        (dimension) =>
          dimension.dimensionType?.code !== DataObjectDimensionType.KEY_BASED,
      );
      if (noKeyDimensions.length !== dimensions.length) {
        form.setValue('dimensions', renumberDimensions(noKeyDimensions));
      }
    }
  }, [dataObjectView, form, outputTypeCode]);

  return (
    <Flex align="end" gap="middle" vertical>
      <TanstackFormTable
        name="dimensions"
        columns={cols}
        options={filters}
        defaultSort={[{ id: 'sequenceNumber', desc: false }]}
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
    </Flex>
  );
}

function useDimensionTable(
  dataObjectView: DataObjectView,
  defaultStatusCode: string,
  subjects: string[],
  outputTypeCode?: DataObjectOutputType,
  dataSubjectAttributes: { code: string; name: string }[] = [],
) {
  const validPeriod: [string | null, string | null] = useWatch({
    name: 'validPeriod',
  });
  const { t } = useTranslation();
  const isDraft = defaultStatusCode === 'DRAFT';

  const cols = useMemo(
    () =>
      getColumns(
        t,
        dataObjectView,
        isDraft,
        outputTypeCode,
        subjects,
        validPeriod,
        isDraft ? (
          <CreateDimensionRowButton
            subjectAttributeDimensions={dataSubjectAttributes}
          />
        ) : undefined,
      ),
    [
      dataObjectView,
      dataSubjectAttributes,
      isDraft,
      subjects,
      t,
      validPeriod,
      outputTypeCode,
    ],
  );

  const filters = useMemo(
    () => ({
      dimensionType: Object.values(DataObjectDimensionType).map((code) => {
        const label = t(`field.dimensionType.${code}`);
        return {
          id: label,
          name: label,
        };
      }),
    }),
    [t],
  );

  return { cols, filters, isDraft };
}
